import React, { useState, useEffect, useCallback } from "react";
import { GoogleMap, Marker, Polyline } from "@react-google-maps/api"; // ⛔ Removed LoadScript
import { getAddressFromCoords } from "../../utils/getAddress";

interface LocationMapProps {
  onFromLocationSelect: (
    coords: google.maps.LatLngLiteral,
    address: string
  ) => void;
  onToLocationSelect: (
    coords: google.maps.LatLngLiteral,
    address: string
  ) => void;
  initialFromLocation?: google.maps.LatLngLiteral;
  initialToLocation?: google.maps.LatLngLiteral;
}

const containerStyle = {
  width: "100%",
  height: "400px",
};

const defaultCenter = {
  lat: 23.04378750484091,
  lng: 72.58426946230855,
};

const LocationMap: React.FC<LocationMapProps> = ({
  onFromLocationSelect,
  onToLocationSelect,
  initialFromLocation,
  initialToLocation,
}) => {
  const [fromLocation, setFromLocation] = useState(initialFromLocation || null);
  const [toLocation, setToLocation] = useState(initialToLocation || null);
  const [directions, setDirections] =
    useState<google.maps.DirectionsResult | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setFromLocation(initialFromLocation || null);
    setToLocation(initialToLocation || null);
  }, [initialFromLocation, initialToLocation]);

  useEffect(() => {
    if (fromLocation && toLocation && map && window.google?.maps) {
      const directionsService = new window.google.maps.DirectionsService();

      directionsService.route(
        {
          origin: fromLocation,
          destination: toLocation,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (
          result: google.maps.DirectionsResult | null,
          status: google.maps.DirectionsStatus
        ) => {
          if (status === window.google.maps.DirectionsStatus.OK && result) {
            setDirections(result);
          } else {
            return;
          }
        }
      );
    } else {
      setDirections(null);
    }
  }, [fromLocation, toLocation, map]);

  useEffect(() => {
    if (map) {
      if (fromLocation && toLocation) {
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(new window.google.maps.LatLng(fromLocation.lat, fromLocation.lng));
        bounds.extend(new window.google.maps.LatLng(toLocation.lat, toLocation.lng));
        map.fitBounds(bounds);
      } else if (fromLocation || toLocation) {
        map.setCenter(fromLocation || toLocation!);
        // map.setZoom(16);
      } else {
        map.setCenter(defaultCenter);
        // map.setZoom(14);
      }
    }
  }, [map, fromLocation, toLocation]);

  const handleMapClick = useCallback(
    async (e: google.maps.MapMouseEvent) => {
      if (e.latLng && !isLoading) {
        setIsLoading(true);
        const coords = {
          lat: e.latLng.lat(),
          lng: e.latLng.lng(),
        };

        try {
          const address = await getAddressFromCoords(coords);
          // Always set the starting location on click
          setFromLocation(coords);
          onFromLocationSelect(coords, address);
          // If a toLocation exists, clear it along with directions
          if (toLocation) {
            setToLocation(null);
            setDirections(null);
            onToLocationSelect({ lat: 0, lng: 0 }, "");
          }
        } catch (err) {
          console.error("Geocoding error:", err);
        } finally {
          setIsLoading(false);
        }
      }
    },
  [isLoading,onFromLocationSelect,onToLocationSelect,toLocation]
  );

  const mapOptions: google.maps.MapOptions = {
    styles: [
      {
        featureType: "all",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
      },
    ],
    mapTypeId: "terrain",
  };

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={defaultCenter}
      zoom={14}
      onClick={handleMapClick}
      onLoad={setMap}
      options={mapOptions}
    >
      {fromLocation && (
        <Marker
          position={fromLocation}
          icon={{
            url: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
            scaledSize: new window.google.maps.Size(40, 40),
          }}
        />
      )}
      {/* {toLocation && (
        <Marker
          position={toLocation}
          icon={{
            url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
            scaledSize: new window.google.maps.Size(40, 40),
          }}
        />
      )} */}
      {fromLocation && toLocation && (
        <Polyline
          path={[fromLocation, toLocation]}
          options={{
            strokeColor: "#004976",
            strokeOpacity: 0.8,
            strokeWeight: 6,
          }}
        />
      )}
    </GoogleMap>
  );
};

export default LocationMap;
